import {action, makeAutoObservable, reaction} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {IUser, IUserStore} from "data/stores/user/user.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {ITeamStore} from "data/stores/team/team.store";
import type {ITeamResponse, IUserTeamResponse} from "data/providers/api/team.api.provider";
import type {IRound, IRoundsStore} from "data/stores/rounds/rounds.store";
import type {IRankingsStore} from "data/stores/rankings/rankings.store";
import type {IGamebarResponse} from "data/providers/api/rankings.api.provider";
import type {IModalsStore} from "data/stores/modals/modals.store";

export interface IUserBarController extends ViewController {
	get i18n(): ILocalizationStore;
	get user(): IUser | undefined;
	get teamObject(): ITeamResponse;
	get selectedRound(): IRound | undefined;
	get gamebarData(): IGamebarResponse | null;
	get isTradeStarted(): boolean;
	get userTeam(): IUserTeamResponse;

	onCompleteCountdown: () => void;
}

@injectable()
export class UserBarController implements IUserBarController {
	private _disposerReaction!: ReturnType<typeof reaction>;

	get i18n() {
		return this._i18nStore;
	}

	get user() {
		return this._userStore.user;
	}

	get teamObject() {
		return this._teamStore.teamObject;
	}

	get selectedRound() {
		return this._roundStore.selectedRound;
	}

	get gamebarData() {
		return this._rankingStore.gamebar;
	}

	get isTradeStarted() {
		return this._teamStore.isTradeStarted;
	}

	get userTeam() {
		return this._teamStore.userTeam;
	}

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.TeamStore) private _teamStore: ITeamStore,
		@inject(Bindings.RankingsStore) private _rankingStore: IRankingsStore,
		@inject(Bindings.ModalsStore) private _modalStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	@action onCompleteCountdown = () => {
		void this._roundStore.fetchRounds();
	};

	dispose() {
		this._disposerReaction?.();
	}

	async init(param: void) {
		if (this._roundStore.selectedRoundId) {
			await this._rankingStore.fetchGamebar({
				gamedayId: this._roundStore.selectedRoundId,
			});
		}

		this._disposerReaction = reaction(
			() => this._roundStore.selectedRoundId,
			(roundId) => {
				void this._rankingStore.fetchGamebar({
					gamedayId: roundId,
				});
			},
			{fireImmediately: false}
		);
	}
}
